/**
 * Central messaging source of truth for BizRnR's two-pillar brand system.
 *
 * Decomposed barrel — every public symbol formerly exported from
 * `messaging-bank.ts` is preserved through `export * from './<section>'`.
 * Phase 0.7 wave 1 (2026-04-28, issue #1005) split the 1,788-line file
 * into 11 section files (each ≤400 LOC) so max-lines:400 enforcement
 * (Hard Rule 8) holds. Hardcoded copy guards (V4 charter) continue to
 * resolve via `@/lib/marketing/messaging-bank` — TS module resolution
 * picks up this index automatically.
 *
 * Section breakdown:
 *   - brand               — BRAND, PILLAR_1, PILLAR_2, PILLARS, CATEGORY_CONTRASTS, AI_SEARCH_PLATFORMS
 *   - shared-ctas         — DEFAULT_PRIMARY_CTA, utmCta, SECONDARY_CTA_*, SHARED_FINAL_CTA_SECTION
 *   - v4-brand            — V4 pillars, V4_BRAND, V4_BRAND_SENTENCE, rotation helpers
 *   - page-copy-types     — PageCopy interface
 *   - pillar-pages        — SIMPLE_SETUP_COPY, AI_BUSINESS_PAGE_COPY
 *   - industries-vs       — INDUSTRIES_*, VS_*
 *   - blog-faq            — BLOG_*, FAQ_COPY
 *   - pricing-legal       — PRICING_*, LEGAL_*
 *   - reports-tools       — REPORTS_*, TOOLS_*
 *   - integrations-business — INTEGRATIONS_*, BUSINESS_*
 *   - case-studies-ai     — CASE_STUDIES_*, AI_VOICE_*, AI_CONTEXT_COPY
 *   - redirects-near-me   — DOUBLE_DOWN_COPY, ALWAYS_*, NEAR_ME_COPY, REQUEST_DEMO_COPY, DEMO_COPY
 *   - misc-pages          — PARTNERS, PRESS, LP_AI_DISCOVERY, GUIDES_*, STATUS, SUBSCRIPTION_EXPIRED, L_SLUG
 *   - trust-pages         — CONTACT, SECURITY, CUSTOMERS, CAREERS
 *   - lp-industry         — LP_HVAC, LP_DENTAL, LP_LAW, LP_PLUMBING
 */

export * from './brand';
export * from './shared-ctas';
export * from './v4-brand';
export * from './page-copy-types';
export * from './pillar-pages';
export * from './industries-vs';
export * from './blog-faq';
export * from './pricing-legal';
export * from './reports-tools';
export * from './integrations-business';
export * from './case-studies-ai';
export * from './redirects-near-me';
export * from './misc-pages';
export * from './trust-pages';
export * from './ai-receptionist';
export * from './ai-voice-receptionist';
export * from './lp-industry';
export * from './video-section';
export * from './hero-demo-script';
export * from './ask-ai';
export * from './authority-evidence';

import type { PageCopy } from './page-copy-types';
import { SIMPLE_SETUP_COPY, AI_BUSINESS_PAGE_COPY } from './pillar-pages';
import {
  INDUSTRIES_COPY,
  INDUSTRIES_SLUG_COPY,
  VS_COPY,
  VS_COMPETITOR_COPY,
} from './industries-vs';
import { BLOG_INDEX_COPY, BLOG_SLUG_COPY, FAQ_COPY } from './blog-faq';
import {
  PRICING_COPY,
  PRICING_AI_RECEPTIONIST_COST_COPY,
  PRICING_CUSTOM_COPY,
  LEGAL_PRIVACY_COPY,
  LEGAL_TERMS_COPY,
} from './pricing-legal';
import {
  REPORTS_MISSED_CALL_COPY,
  REPORTS_STATE_OF_AI_COPY,
  TOOLS_MISSED_CALL_ROI_COPY,
  TOOLS_VOICE_SCRIPT_COPY,
} from './reports-tools';
import {
  INTEGRATIONS_INDEX_COPY,
  INTEGRATIONS_PARTNER_COPY,
  BUSINESS_INDEX_COPY,
  BUSINESS_SLUG_COPY,
} from './integrations-business';
import {
  CASE_STUDIES_INDEX_COPY,
  CASE_STUDIES_SLUG_COPY,
  AI_VOICE_COPY,
  AI_VOICE_AGENT_COPY,
  AI_CONTEXT_COPY,
} from './case-studies-ai';
import {
  DOUBLE_DOWN_COPY,
  ALWAYS_ANSWERS_COPY,
  ALWAYS_OPEN_COPY,
  NEAR_ME_COPY,
  REQUEST_DEMO_COPY,
  DEMO_COPY,
} from './redirects-near-me';
import {
  PARTNERS_COPY,
  PRESS_COPY,
  LP_AI_DISCOVERY_COPY,
  GUIDES_AI_VOICE_AGENT_COPY,
  STATUS_COPY,
  SUBSCRIPTION_EXPIRED_COPY,
  L_SLUG_COPY,
} from './misc-pages';
import {
  CONTACT_COPY,
  SECURITY_COPY,
  CUSTOMERS_COPY,
  CAREERS_COPY,
} from './trust-pages';
import { AI_RECEPTIONIST_COPY } from './ai-receptionist';
import { AI_VOICE_RECEPTIONIST_COPY } from './ai-voice-receptionist';
import {
  LP_HVAC_COPY,
  LP_DENTAL_COPY,
  LP_LAW_COPY,
  LP_PLUMBING_COPY,
  LP_REAL_ESTATE_COPY,
} from './lp-industry';

/**
 * Master registry — every marketing page COPY export.
 * Imported by the unit test to assert each entry is non-empty and shape-conformant.
 */
export const MARKETING_PAGE_COPIES = {
  SIMPLE_SETUP_COPY,
  AI_BUSINESS_PAGE_COPY,
  INDUSTRIES_COPY,
  INDUSTRIES_SLUG_COPY,
  VS_COPY,
  VS_COMPETITOR_COPY,
  BLOG_INDEX_COPY,
  BLOG_SLUG_COPY,
  FAQ_COPY,
  PRICING_COPY,
  PRICING_AI_RECEPTIONIST_COST_COPY,
  PRICING_CUSTOM_COPY,
  LEGAL_PRIVACY_COPY,
  LEGAL_TERMS_COPY,
  REPORTS_MISSED_CALL_COPY,
  REPORTS_STATE_OF_AI_COPY,
  TOOLS_MISSED_CALL_ROI_COPY,
  TOOLS_VOICE_SCRIPT_COPY,
  INTEGRATIONS_INDEX_COPY,
  INTEGRATIONS_PARTNER_COPY,
  BUSINESS_INDEX_COPY,
  BUSINESS_SLUG_COPY,
  CASE_STUDIES_INDEX_COPY,
  CASE_STUDIES_SLUG_COPY,
  AI_VOICE_COPY,
  AI_VOICE_AGENT_COPY,
  AI_CONTEXT_COPY,
  DOUBLE_DOWN_COPY,
  ALWAYS_ANSWERS_COPY,
  ALWAYS_OPEN_COPY,
  NEAR_ME_COPY,
  REQUEST_DEMO_COPY,
  DEMO_COPY,
  PARTNERS_COPY,
  PRESS_COPY,
  LP_AI_DISCOVERY_COPY,
  GUIDES_AI_VOICE_AGENT_COPY,
  STATUS_COPY,
  SUBSCRIPTION_EXPIRED_COPY,
  L_SLUG_COPY,
  CONTACT_COPY,
  SECURITY_COPY,
  CUSTOMERS_COPY,
  CAREERS_COPY,
  AI_RECEPTIONIST_COPY,
  AI_VOICE_RECEPTIONIST_COPY,
  LP_HVAC_COPY,
  LP_DENTAL_COPY,
  LP_LAW_COPY,
  LP_PLUMBING_COPY,
  LP_REAL_ESTATE_COPY,
} as const satisfies Readonly<Record<string, PageCopy>>;
